import type { CreatePolicyInput, PolicyLabel } from '@communities/content-management/domain';
import { WorkflowType } from '@communities/content-management/domain';
import { LabelSource } from '@core/graphql/types/schema';

import type { DialogFormData } from '../types';

/**
 * Transform dialog form data to policy input for API calls.
 * Handles both classification (standard labels with severity/score) and
 * moderation (standard + custom labels with presence-based evaluation).
 */
export function transformDialogDataToPolicyInput(formData: DialogFormData, workflowType: WorkflowType): CreatePolicyInput {
  const isModeration = workflowType === WorkflowType.MODERATION;

  // Transform text labels (always severity-based for both workflows)
  const textPolicyLabels: PolicyLabel[] = formData.textLabels.textLabels.map(label => ({
    labelId: label.labelId,
    severity: label.severity,
  }));

  // Transform image labels
  // Classification: score-based | Moderation: presence-based (no score)
  const imagePolicyLabels: PolicyLabel[] = formData.imageLabels.imageLabels.map(label => {
    if (isModeration) {
      // Moderation: presence-based, no scoreThreshold
      return { labelId: label.labelId };
    }

    // Classification: score threshold-based
    return { labelId: label.labelId, scoreThreshold: label.scoreThreshold };
  });

  // Transform username labels (always severity-based, like text labels)
  const usernamePolicyLabels: PolicyLabel[] = formData.usernameLabels.usernameLabels.map(label => ({
    labelId: label.labelId,
    severity: label.severity,
  }));

  // Transform custom labels (Custom source, presence-based, no severity/score)
  const customPolicyLabels: PolicyLabel[] = formData.customLabels.customLabels.map(label => ({
    labelId: label.labelId,
    source: LabelSource.Custom,
  }));

  return {
    name: formData.policyInfo.name.trim(),
    description: formData.policyInfo.description?.trim() || undefined,
    isEnabled: formData.policyInfo.isEnabled,
    labels: [...textPolicyLabels, ...imagePolicyLabels, ...usernamePolicyLabels, ...customPolicyLabels],
    workflowType,
  };
}
